import { useEffect, useState } from 'react'
import { create } from 'zustand'

export const DEFAULT_DETECTION_LOADER_SECONDS = 5
export const MAX_DETECTION_LOADER_SECONDS = 300

export const resolveDetectionLoaderSeconds = (configured: string | undefined): number => {
  const seconds = Number(configured)
  if (configured === undefined || configured === '' || !Number.isFinite(seconds) || seconds <= 0) {
    return DEFAULT_DETECTION_LOADER_SECONDS
  }
  return Math.min(seconds, MAX_DETECTION_LOADER_SECONDS)
}

export const detectionLoaderMs = () =>
  resolveDetectionLoaderSeconds(import.meta.env.VITE_INCOME_DETECTION_LOADER_SECONDS) * 1000

// Detection runs in the background with no completion signal, so after the loader ends the income is
// read again every INCOME_REFRESH_INTERVAL_MS until INCOME_REFRESH_WINDOW_MS after the request.
export const INCOME_REFRESH_INTERVAL_MS = 30_000
export const INCOME_REFRESH_WINDOW_MS = 120_000

interface DetectionProgressState {
  // When the loader ends, per customer.
  until: Record<string, number>
  // When the follow-up income reloads stop, per customer.
  refreshUntil: Record<string, number>
  start: (userId: string) => void
}

export const useDetectionProgressStore = create<DetectionProgressState>()(set => ({
  until: {},
  refreshUntil: {},
  start: userId =>
    set(state => {
      const now = Date.now()
      return {
        until: { ...state.until, [userId]: now + detectionLoaderMs() },
        refreshUntil: { ...state.refreshUntil, [userId]: now + INCOME_REFRESH_WINDOW_MS },
      }
    }),
}))

export const useDetectionInProgress = (userId: string | undefined): boolean => {
  const until = useDetectionProgressStore(state => (userId ? state.until[userId] : undefined))
  const [, setTick] = useState(0)
  const remaining = until ? until - Date.now() : 0

  useEffect(() => {
    if (remaining <= 0) return
    const timer = setTimeout(() => setTick(tick => tick + 1), remaining)
    return () => clearTimeout(timer)
  }, [until, remaining])

  return remaining > 0
}
